/** 前后端共用的类型。这个文件不允许 import 任何 DOM 或 Worker 专有的东西。 */

/** 形如 '2026-08-08'。全项目的日期都用它，不用 Date 对象做算术。 */
export type ISODate = string

export type Flow = 'spot' | 'light' | 'medium' | 'heavy'
export type Protection = 'none' | 'condom' | 'other'
export type LhResult = 'neg' | 'high' | 'peak'
export type Role = 'tracker' | 'partner'

/**
 * 界面语言。前后端共用一套代码：服务端的报错和找回口令的邮件都按它来。
 * 加一门语言时这里加一个代码，再补一份前端字典和一份服务端报错表。
 */
export const LOCALES = ['zh', 'en'] as const
export type Locale = (typeof LOCALES)[number]

/** 认不出来就退回英文 —— 看不懂的英文比看不懂的中文通用。 */
export function toLocale(v: unknown): Locale {
  if (typeof v !== 'string') return 'en'
  const primary = v.toLowerCase().split('-')[0] as Locale
  return (LOCALES as readonly string[]).includes(primary) ? primary : 'en'
}

export interface PeriodLog {
  date: ISODate
  flow: Flow
  note?: string | null
}

export interface IntimacyLog {
  id: string
  date: ISODate
  protection: Protection
  note?: string | null
  createdBy: string
}

export interface SymptomLog {
  date: ISODate
  symptoms: string[]
  /** -2 很差 .. 2 很好 */
  mood?: number | null
  /** 1 .. 5 */
  energy?: number | null
  note?: string | null
}

export interface BbtLog {
  date: ISODate
  tempC: number
}

export interface LhLog {
  date: ISODate
  result: LhResult
}

export interface HouseholdSettings {
  name: string
  timezone: string
  defaultCycleLen: number
  defaultPeriodLen: number
  trackBbt: boolean
  trackLh: boolean
}

export interface SessionUser {
  id: string
  householdId: string
  loginName: string
  displayName: string
  /** 选填。没填就用不了邮箱自助找回，只能找管理员人工重置。 */
  email: string | null
  role: Role
  /** 这个人自己的界面语言。同一个家庭里两个人可以各用各的。 */
  locale: Locale
}

export interface Member {
  id: string
  displayName: string
  role: Role
}

/** solo = 一个人记录，couple = 已解锁配偶席位。由 seats 推导，不单独存。 */
export type Plan = 'solo' | 'couple'

export interface HouseholdPlan {
  plan: Plan
  /** 允许的成员数上限 */
  seats: number
  memberCount: number
  /** 还有空位，可以发邀请 */
  canInvite: boolean
}

/** 一次拉全量数据，两个人的记录量很小，分页纯属自找麻烦。 */
export interface Snapshot {
  user: SessionUser
  settings: HouseholdSettings
  plan: HouseholdPlan
  members: Member[]
  periods: PeriodLog[]
  intimacy: IntimacyLog[]
  symptoms: SymptomLog[]
  bbt: BbtLog[]
  lh: LhLog[]
  /** 服务端按家庭时区算出的今天，前端不自己猜 */
  today: ISODate
}

export interface InviteInfo {
  code: string
  expiresAt: string
}

/** 症状标签的稳定 key，展示文案在 i18n 字典里。 */
export const SYMPTOM_KEYS = [
  'cramps',
  'headache',
  'backache',
  'breastTender',
  'bloating',
  'acne',
  'nausea',
  'insomnia',
  'appetite',
  'discharge',
] as const

export type SymptomKey = (typeof SYMPTOM_KEYS)[number]
